import Papa from 'papaparse';
import type { Curve, ParsedData } from '../../../interfaces/toolData';
import type { WaveFileType } from '../types';
import { cleanTimeSeriesData } from '../../dataQuality';

interface PasonConfig {
  fileType: WaveFileType;
  columnMapping: Record<string, { name: string; unit: string }>;
}

const PASON_EDR_CONFIG: PasonConfig = {
  fileType: 'PASON_EDR_DEFAULT',
  columnMapping: {
    'Hole Depth': { name: 'Hole Depth', unit: 'ft' },
    'Bit Depth': { name: 'Bit Depth', unit: 'ft' },
    'Weight On Bit': { name: 'Weight On Bit', unit: 'klb' },
    'Torque': { name: 'Torque', unit: 'ft-lb' },
    'SPP': { name: 'SPP', unit: 'psi' },
    'ROP': { name: 'ROP', unit: 'ft/hr' }
  }
};

/**
 * Parser for Pason Electronic Drilling Recorder (EDR) files
 * Handles tab-separated drilling data with timestamp columns
 */
export class PasonParser {
  /**
   * Parse Pason EDR file content into standardized format
   */
  static parse(fileContent: string): ParsedData {
    const lines = fileContent.split('\n').filter(line => line.trim());
    
    if (lines.length < 8) {
      throw new Error('Invalid Pason EDR file: insufficient data');
    }

    // Validate file format by checking header
    if (!lines[0].includes('Pason PENLESS Drilling Recorder')) {
      throw new Error('Invalid Pason EDR file: missing expected header');
    }

    // Extract metadata from header section
    const metadata = this.extractMetadata(lines.slice(0, 7));
    
    // Find header line and data section
    let headerLineIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('DATE') && lines[i].includes('TIME') && lines[i].includes('Hole Depth')) {
        headerLineIndex = i;
        break;
      }
    }

    if (headerLineIndex === -1) {
      throw new Error('Invalid Pason EDR file: could not find data header');
    }

    // Parse CSV data section
    const csvContent = lines.slice(headerLineIndex).join('\n');
    const parseResult = Papa.parse(csvContent, {
      header: true,
      skipEmptyLines: true,
      delimiter: '\t'
    });

    if (parseResult.errors.length > 0) {
      throw new Error(`CSV parsing error: ${parseResult.errors[0].message}`);
    }

    const data = parseResult.data as Record<string, string>[];
    
    if (data.length === 0) {
      throw new Error('No data rows found in Pason EDR file');
    }

    // Generate curves from data
    const curves = this.generateCurves(data);

    return {
      fileType: PASON_EDR_CONFIG.fileType,
      toolType: 'Pason',
      curves,
      metadata,
      errors: []
    };
  }

  /**
   * Extract metadata from file header
   */
  private static extractMetadata(headerLines: string[]): Record<string, string> {
    const metadata: Record<string, string> = {};

    headerLines.forEach(line => {
      if (line.includes(':')) {
        const parts = line.split(':');
        if (parts.length >= 2) {
          const key = parts[0].trim();
          const value = parts.slice(1).join(':').trim();
          if (key && value) {
            metadata[key] = value;
          }
        }
      }
    });

    return metadata;
  }

  /**
   * Generate time series curves from parsed data
   */
  private static generateCurves(data: Record<string, string>[]): Curve[] {
    const curves: Curve[] = [];
    
    // Parse timestamps from DATE and TIME columns
    const timestamps: Date[] = [];
    const validRows: Record<string, string>[] = [];

    for (const row of data) {
      const dateStr = row['DATE']?.trim();
      const timeStr = row['TIME']?.trim();
      
      if (!dateStr || !timeStr) continue;

      try {
        // Parse date and time into a single timestamp
        const timestamp = new Date(`${dateStr} ${timeStr}`);
        
        // Validate timestamp (2010 to today, no future dates)
        const now = new Date();
        const minDate = new Date('2010-01-01');
        
        if (timestamp < minDate || timestamp > now) {
          continue; // Skip invalid dates
        }

        timestamps.push(timestamp);
        validRows.push(row);
      } catch (error) {
        continue; // Skip rows with invalid timestamps
      }
    }

    if (timestamps.length === 0) {
      throw new Error('No valid timestamp data found');
    }

    // Create curves for each measurement column
    Object.entries(PASON_EDR_CONFIG.columnMapping).forEach(([columnName, config]) => {
      const values: number[] = [];
      const curveTimestamps: Date[] = [];

      validRows.forEach((row, index) => {
        const valueStr = row[columnName]?.trim();
        if (valueStr) {
          const value = parseFloat(valueStr);
          if (!isNaN(value) && this.isValidValue(columnName, value)) {
            values.push(value);
            curveTimestamps.push(timestamps[index]);
          }
        }
      });

      if (values.length > 0) {
        const curve: Curve = {
          name: config.name,
          xValues: curveTimestamps,
          yValues: values,
          yUnit: config.unit
        };

        // Apply data quality cleanup
        const cleanupResult = cleanTimeSeriesData(curve);
        curves.push(cleanupResult.cleanedCurve);
      }
    });

    if (curves.length === 0) {
      throw new Error('No valid measurement data found');
    }

    return curves;
  }

  /**
   * Validate measurement values against expected ranges
   */
  private static isValidValue(columnName: string, value: number): boolean {
    // Depth validation (0 to 100,000 ft)
    if (columnName === 'Hole Depth' || columnName === 'Bit Depth') {
      return value >= 0 && value <= 100000;
    }
    
    // Weight on Bit validation (0 to 200,000 klb)
    if (columnName === 'Weight On Bit') {
      return value >= 0 && value <= 200000;
    }
    
    // Torque validation (0 to 200,000 ft-lb)
    if (columnName === 'Torque') {
      return value >= 0 && value <= 200000;
    }
    
    // SPP validation (0 to 100,000 psi)
    if (columnName === 'SPP') {
      return value >= 0 && value <= 100000;
    }
    
    // ROP validation (0 to 10,000 ft/hr)
    if (columnName === 'ROP') {
      return value >= 0 && value <= 10000;
    }

    // Default: allow any finite number
    return isFinite(value);
  }

  /**
   * Check if file content appears to be a Pason EDR file
   */
  static canParse(fileContent: string): boolean {
    const firstLine = fileContent.split('\n')[0]?.trim();
    return firstLine?.includes('Pason PENLESS Drilling Recorder') || false;
  }
} 